import { SPACE_TYPES, createRange } from "@circleofa/acim-engine"
import type { DisplayWord } from "@circleofa/acim-engine"
import { Capacitor } from "@capacitor/core"
import { registerPlugin } from "@capacitor/core"
import { get, readable } from "svelte/store"
import { Share as CapacitorShare } from "@capacitor/share"
import { isNative } from "@utils/underhood/browser"

interface CopyHtmlPlugin {
    copyHtml(options: { html: string; text: string }): Promise<void>
}
const CopyHtml = registerPlugin<CopyHtmlPlugin>("CopyHtml")

export type WordSettings = Parameters<DisplayWord["getCustomWord"]>[0]

const addlink = true

export function copyHtml(html: string, fallback: string) {
    console.log("attempting to copy", html, fallback)
    const htmlBlob = new Blob([html], { type: "text/html" })
    const plainBlob = new Blob([fallback], { type: "text/plain" })
    const htmlClipboard = new ClipboardItem({
        "text/html": htmlBlob,
        "text/plain": plainBlob,
    })
    if (Capacitor.getPlatform() === "android") {
        return CopyHtml.copyHtml({ html: html, text: fallback })
    } else {
        return navigator.clipboard.write([htmlClipboard])
    }
}

/**
 * TODO upgrade to Effect
 */
export class CopyWord {
    #prettyPath = ""
    constructor(public readonly words: DisplayWord[]) {}

    get firstWord() {
        return this.words[0]
    }

    get lastWord() {
        return this.words[this.words.length - 1]
    }

    html(settings: WordSettings = {}) {
        const html = this.words
            .map((word, i) => {
                // Previous word
                const previousWord = this.words[Math.max(0, i - 1)]
                const previousContainer =
                    previousWord.parentLine.parentPiece || previousWord.actualParent

                // Check if same or different piece, title or paragraph from last -
                const thisContainer = word.parentLine.parentPiece || word.actualParent

                const differentContainer = previousContainer != thisContainer

                return (
                    (differentContainer ? "<br><br>" : "") +
                    word.getCustomWord(settings) +
                    (word.space === SPACE_TYPES.SPACE_UNDERLINE ? "<u> </u>" : " ")
                )
            })
            .join("")

            // This should makes the string more pretty
            .replace(/<u> <\/u>$/g, " ")
            .trim()
            .replaceAll("</u><u>", "")
            .replaceAll("</b><b>", "")
            .replaceAll("</b> <b>", " ")
            .replaceAll("</i> <i>", " ")
            .replaceAll("</i><i>", "")
        return html
    }

    get prettyPath() {
        if (this.#prettyPath) return this.#prettyPath
        const prettyPath = createRange(this.firstWord.parentWeight, this.lastWord.parentWeight)
        this.#prettyPath = prettyPath
        return prettyPath
    }

    /**
     * Get the link for the copy.
     * @returns The link for the copy. e.g. https://acimce.app/:T-2
     */
    get link() {
        return "https://acimce.app/:" + this.prettyPath
    }

    /**
     * Get the HTML link for the copy.
     * @returns The HTML link for the copy. e.g. <a href="https://acimce.app/:1-2">[CE 1-2]</a>
     */
    get htmlLink() {
        return `<a href="${this.link}">[CE ${this.prettyPath}]</a>`
    }

    totalHtml(includeReference = false, wordSettings: WordSettings = {}) {
        const html = this.html(wordSettings)
        const hasParagraphBreaks = !!/<br>/.exec(html)
        return (
            html + (includeReference ? (hasParagraphBreaks ? "<br><br>" : " ") + this.htmlLink : "")
        )
    }

    copy(includeReference = false, wordSettings: WordSettings = {}) {
        const htmlTotal = this.totalHtml(includeReference, wordSettings)
        const plainString = stripHTML(htmlTotal + (addlink ? " " + this.link : ""))

        return copyHtml(htmlTotal, plainString)
    }

    async share(settings: WordSettings = {}) {
        if (!get(canShare)) {
            throw new Error("Can't share!")
        }

        console.log("Attempting regular share")

        const html = this.html(settings)
        const data = {
            url: this.link,
            text: stripHTML(html),
            title: "A Course in Miracles " + this.prettyPath,
        }
        const capVersion = { ...data, dialogueTitle: "Share" }

        if (await CapacitorShare.canShare()) {
            console.log("Can share native...")
            /** This will catch if the user cancels */
            return CapacitorShare.share(capVersion)
        } else {
            console.log("Cannot share native")
            if (navigator.canShare && navigator.canShare(data)) {
                console.log("Sharing web")
                return navigator.share(data)
            } else {
                throw new Error("Whoops! Something went wrong with sharing!")
            }
        }
    }
}

/**
 * Checks the navigator (web) and capacitor (native) APIs
 * TODO upgrade to effect
 */
export const canShare = readable<null | boolean>(null, (set) => {
    CapacitorShare.canShare().then(({ value: capShare }) => {
        const canShareNative = isNative && capShare
        const canShareNavigator =
            navigator.canShare &&
            navigator.canShare({
                url: "https://acimce.app",
                text: "Sample text",
                title: "Sample title",
            })
        console.log({ canShareNavigator, canShareNative })
        set(canShareNavigator || canShareNative)
    })
})

function stripHTML(str: string) {
    return str.replaceAll("<br>", "\n").replace(/<.+?>/g, "")
}

// /**
//  * =========================================================================
//  * 🏞️ Image Manipulation
//  * =========================================================================
//  */

// /**
//  * -------------------------------------------------------------------------
//  * 💾 Download
//  * -------------------------------------------------------------------------
//  */

// /**
//  * Downloads an object in the browser
//  * @param path path to fetch
//  * @param filename filename to download
//  */
// export async function download(path: string, filename: string) {
//     console.log({ path })
//     const res = await fetch(path)
//     const blob = await res.blob()

//     let blobPath: string
//     if (isNative) {
//         const reader = new FileReader()
//         blobPath = await new Promise<string>((res, rej) => {
//             reader.onloadend = async function () {
//                 const base64data = reader.result
//                 await Filesystem.writeFile({
//                     path: filename,
//                     data: base64data as string,
//                     directory: Directory.Documents, // you may choose a different directory if you want
//                     recursive: true, // directories are created if they do not exist
//                 })

//                 const uri = await Filesystem.getUri({
//                     directory: Directory.Documents,
//                     path: filename,
//                 })

//                 blobPath = uri.uri
//             }

//             reader.onerror = rej

//             reader.readAsDataURL(blob)
//         })
//     } else {
//         blobPath = window.URL.createObjectURL(blob)
//     }
//     const link = document.createElement("a")
//     link.href = blobPath
//     link.download = filename // Specify the desired file name for the downloaded image
//     link.target = "_blank"
//     console.log(link)
//     link.click()

//     if (!isNative) window.URL.revokeObjectURL(blobPath)
// }

// /**
//  * -------------------------------------------------------------------------
//  * 📋 Copy Image to Clipboard
//  * -------------------------------------------------------------------------
//  */

// /**
//  * Only accepts jpeg
//  * @param imageURL
//  * @param appLink
//  * @param title
//  * @returns
//  */
// export async function sharePicture(imageURL: string, appLink: string, title: string, text: string) {
//     const data = {
//         url: appLink,
//         title,
//         text,
//     }

//     if ((await Share.canShare()) && isNative) {
//         // iOS doesn't play nicely with the image
//         console.log("native share mode")
//         const localPath = await writeFileFromURL(imageURL)
//         const capVersion = { ...data, dialogueTitle: "Share", files: [localPath] }
//         /** This will catch if the user cancels */
//         return Share.share(capVersion)
//     } else {
//         console.log(" web share mode!!")
//         if (!navigator.canShare) throw new Error("Navigator sharing not allowed")
//         const res = fetch(imageURL)
//         const blob = await (await res).blob()
//         const file = new File([blob], "image.jpg", { type: blob.type })
//         const webVersion = { ...data, files: [file] }
//         console.log(webVersion)
//         if (navigator.canShare(data)) {
//             return navigator.share(webVersion)
//         } else {
//             throw new Error("Whoops! Something went wrong with sharing!")
//         }
//     }
// }

// async function writeFileFromURL(imageURL: string) {
//     const response: Response = await fetch(imageURL)
//     const blob = await response.blob()
//     const reader = new FileReader()
//     reader.readAsDataURL(blob)

//     return await new Promise<string>((res, rej) => {
//         reader.addEventListener("loadend", async () => {
//             const file = await Filesystem.writeFile({
//                 data: reader.result as string,
//                 directory: Directory.Cache,
//                 path: `${Date.now()}.jpg`,
//             })

//             res(file.uri)
//         })

//         reader.addEventListener("error", rej)
//     })
// }
